import React, { useImperativeHandle, useRef } from "react";
import { useState } from "react";
import { ethers } from "ethers";
import { useTranslation } from 'react-i18next';
import { NETWORK_LIST, nftAbi, tokenAbi, zeroAddress } from "../../utils/config";
import { nftV2Abi } from './nftV2Config';
import { buildMintCall, formatPaymentAmount } from './paymentConfig';
import { getWalletSnapshot } from '../../utils/walletProviders';
import { getLocalizedWalletError } from './errorMessages';
import { getNetworkDisplayName } from './networkPresentation';
import ConsoleDialog from './ConsoleDialog';

const trimPaymentAmount = (amount, decimals) => {
    if (!amount) return '--'
    return formatPaymentAmount(amount, decimals)
        .replace(/(\.\d*?[1-9])0+$/, '$1')
        .replace(/\.0+$/, '')
}

const shortAddress = (value) => value
    ? `${value.slice(0, 8)}...${value.slice(-6)}`
    : '--'

export default function MintModal(props) {

    const [approveAmountInfo, setApproveAmountInfo] = useState({}); // 授权数量
    const approveAmountInfoRef = useRef({});
    const [currentProvider, setCurrentProvider] = useState(null); // 当前的provider
    const [currentStep, setCurrentStep] = useState(1); // 当前的步骤
    const [approveLoading, setApproveLoading] = useState(false); // 授权loading
    const [stakeLoading, setStakeLoading] = useState(false); // 质押loading
    const [errorMsg, setErrorMsg] = useState(''); // 错误信息
    const [mintReceipt, setMintReceipt] = useState(null)
    // useImperativeHandle(props.)
    const { t } = useTranslation();

    useImperativeHandle(props.onRef, () => {
        // 需要将暴露的接口返回出去
        return {
            setApproveInfo: setApproveInfo,
            handleLogic: handleLogic
        };
    });

    const handleLogic = async () => {
        const info = approveAmountInfoRef.current
        if (info.amount.gt(info.stakeTokenBalanceBigNumber)) {
            setErrorMsg(t('余额不足'))
            return
        }
        // 1. 授权
        const approved = await handleApproveLogic()
        if (!approved) return
        // 2. 质押
        await handleStakeLogic()
        // 3. 显示完成
    }

    const initProvider = () => {
        if (currentProvider) {
            return currentProvider
        }
        const injectedProvider = getWalletSnapshot().provider
        if (injectedProvider && !currentProvider) {
            const provider = new ethers.providers.Web3Provider(injectedProvider)
            setCurrentProvider(provider)
            return provider
        } else {
            console.log('No provider')
        }
    }

    const handleStakeLogic = async () => {
        // setCurrentStep(2)
        setErrorMsg('')
        const p = initProvider()
        setStakeLoading(true)
        const info = approveAmountInfoRef.current
        const mintAbi = Number(info.templateId) === 2 ? nftV2Abi : nftAbi
        const a = new ethers.Contract(info.stakeContract, mintAbi, p.getSigner())
        console.log(zeroAddress)
        let receiver = zeroAddress
        console.log(props.invitor, info.currentWallet)
        if (info.enableInviter && props.invitor && props.invitor !== undefined && props.invitor !== '' && props.invitor.toLowerCase() !== info.currentWallet.toLowerCase()) {
            receiver = props.invitor
        }

        try {
            const mintCall = buildMintCall({
                templateId: info.templateId,
                inviter: receiver,
                amount: ethers.BigNumber.from(info.mintQuantity || 1),
                value: '',
                configVersion: info.configVersion,
            })
            const tx = await a[mintCall.functionName](...mintCall.args)
            console.log('发起质押', tx)
            const receipt = await tx.wait()
            console.log('质押成功', receipt)
            setMintReceipt(receipt)
            setCurrentStep(3)
            return true
        } catch (e) {
            handleErr(e)
            return false
        } finally {
            setStakeLoading(false)
        }
    }

    const handleApproveLogic = async () => {
        setCurrentStep(1)
        setErrorMsg('')
        setApproveLoading(true)
        try {
            const info = approveAmountInfoRef.current
            const p = initProvider()
            console.log(info)
            const approveAmount = info.amount
            const a = new ethers.Contract(info.tokenAddress, tokenAbi, p.getSigner())
            const allowanceAmount = await a.allowance(info.currentWallet, info.stakeContract)
            console.log('授权数量为', allowanceAmount, allowanceAmount.toString(), approveAmount, approveAmount.toString())
            if (approveAmount.gt(allowanceAmount)) {
                const tx = await a.approve(approveAmountInfo.stakeContract, approveAmount)
                console.log('发起授权', tx)
                const receipt = await tx.wait()
                console.log('授权成功', receipt)
            }
            setCurrentStep(2)
            return true
        } catch (e) {
            setCurrentStep(1)
            handleErr(e)
            return false
        } finally {
            setApproveLoading(false)
        }
    }

    const handleErr = (e) => {
        console.log(e)
        const rawMessage = [e?.reason, e?.message, e?.error?.message].filter(Boolean).join(' ').toLowerCase()
        if (rawMessage.includes('stale config')) {
            props.onConfigStale && props.onConfigStale()
        }
        setErrorMsg(getLocalizedWalletError(e, t))
    }

    const setApproveInfo = (info) => {
        return new Promise((resolve, reject) => {
            approveAmountInfoRef.current = info
            setApproveAmountInfo(info)
            setCurrentStep(1)
            setErrorMsg('')
            setMintReceipt(null)
            // if (info.stakeMax) {
            //     // 质押最大, 获取当前的余额

            // } else {
            //     // 质押的不是最大, 使用输入的数量计算
            // }
            resolve()
        })
        //  
    }

    const tokenSymbol = approveAmountInfo.tokenSymbol || t('其他代币')
    const tokenName = approveAmountInfo.tokenName || t('支付代币')
    const mintQuantity = Number(approveAmountInfo.mintQuantity || 1)
    const paymentAmount = trimPaymentAmount(approveAmountInfo.amount, approveAmountInfo.tokenDecimals || 18)
    const network = NETWORK_LIST.find((item) => Number(item.chainId) === Number(approveAmountInfo.chainId))
    const networkName = getNetworkDisplayName(network, approveAmountInfo.chainId, t)
    const isLoading = approveLoading || stakeLoading
    const steps = [
        { id: 1, label: t('授权') },
        { id: 2, label: t('铸造') },
        { id: 3, label: t('完成') },
    ]
    const flowDescription = currentStep === 1
        ? t('钱包将仅授权本次铸造所需的代币数量')
        : currentStep === 2
            ? t('授权已完成，请在钱包中确认铸造交易')
            : t('交易已确认，NFT 已写入您的钱包')
    const primaryLabel = currentStep === 1
        ? (approveLoading ? t('授权中...') : errorMsg ? t('重新授权') : t('授权'))
        : currentStep === 2
            ? (stakeLoading ? t('铸造中...') : t('确认铸造'))
            : t('铸造完成')
    const handlePrimaryAction = () => {
        if (isLoading) return
        if (currentStep === 1) {
            handleApproveLogic()
            return
        }
        if (currentStep === 2) {
            handleStakeLogic()
            return
        }
        props.ok && props.ok(mintReceipt)
        props.handleClose && props.handleClose()
    }


    return <>
        <ConsoleDialog show={props.show} onClose={() => {
            props.handleClose && props.handleClose()
        }} title={t('铸造NFT')} description={t('完成代币授权并提交铸造交易')} icon="fa-rocket" closeLabel={t('关闭')} className="mint-flow-dialog" footer={<>
            {currentStep < 3 && <button type="button" className="console-button mint-flow-action is-secondary" onClick={() => props.handleClose && props.handleClose()} disabled={isLoading}>
                {t('取消')}
            </button>}
            <button type="button" className="console-button mint-flow-action is-primary" onClick={handlePrimaryAction} disabled={isLoading}>
                {isLoading && <i className="fa fa-spinner loading-css" aria-hidden="true" />}
                {!isLoading && <i className={`fa ${currentStep === 3 ? 'fa-check' : currentStep === 2 ? 'fa-cube' : 'fa-shield-alt'}`} aria-hidden="true" />}
                <span>{primaryLabel}</span>
            </button>
        </>}>
            <div className="mint-flow">
                <ol className="mint-flow-steps" data-testid="mint-flow-steps" aria-label={t('铸造进度')}>
                    {steps.map((step, index) => <React.Fragment key={step.id}>
                        <li className={currentStep === step.id ? 'is-active' : currentStep > step.id ? 'is-complete' : ''} aria-current={currentStep === step.id ? 'step' : undefined}>
                            <span>{step.id}</span>
                            <strong>{step.label}</strong>
                            <i className="mint-flow-steps__state" aria-hidden="true" />
                        </li>
                        {index < steps.length - 1 && <i className="fa fa-chevron-right mint-flow-steps__separator" aria-hidden="true" />}
                    </React.Fragment>)}
                </ol>

                <div className={`mint-flow-content ${currentStep === 3 ? 'is-complete' : ''}`}>
                    <div className="mint-flow-asset">
                        <span className="mint-flow-asset__icon"><i className={`fa ${currentStep === 3 ? 'fa-check' : 'fa-coins'}`} /></span>
                        <div>
                            <strong>{currentStep === 3 ? t('铸造成功') : tokenSymbol}</strong>
                            <small>{currentStep === 3 ? t('交易已确认') : tokenName}</small>
                        </div>
                        {currentStep < 3 && <span className="mint-flow-asset__type">{t('其他代币')}</span>}
                    </div>
                    <p className="mint-flow-description">{flowDescription}</p>

                    <dl className="mint-flow-summary">
                        <div>
                            <dt><i className="fa fa-cube" /> {t('铸造数量')}</dt>
                            <dd>{mintQuantity}</dd>
                        </div>
                        <div>
                            <dt><i className="fa fa-coins" /> {t('支付金额')}</dt>
                            <dd>{paymentAmount} {tokenSymbol}</dd>
                        </div>
                        <div>
                            <dt><i className="fa fa-globe" /> {t('网络')}</dt>
                            <dd>{networkName}</dd>
                        </div>
                        {currentStep === 2 && props.invitor && <div>
                            <dt><i className="fa fa-user-plus" /> {t('邀请人')}</dt>
                            <dd title={props.invitor}>{shortAddress(props.invitor)}</dd>
                        </div>}
                    </dl>

                    {errorMsg && <div className="mint-flow-error" role="alert">
                        <i className="fa fa-exclamation-triangle" aria-hidden="true" />
                        <span>{errorMsg}</span>
                        <small>{t('钱包操作未完成，您的资产没有发生变化')}</small>
                    </div>}
                    {isLoading && <div className="mint-flow-pending" role="status">
                        <span className="mint-flow-pending__signal"><i className="fa fa-wallet" /></span>
                        <div>
                            <strong>{currentStep === 1 ? t('请在钱包中确认授权') : t('请在钱包中确认铸造')}</strong>
                            <small>{t('请勿关闭此窗口')}</small>
                        </div>
                    </div>}
                </div>
            </div>
        </ConsoleDialog>
    </>
}
